/**
 * Messaging domain types — shared by the Messages list page, the Conversation
 * thread page, and the compose modal.
 *
 * NOTE: data is currently sourced from a shared in-memory mock store
 * (`@/data/conversations`) — the ERPat SDK has no messaging endpoint yet and
 * auth is bypassed in development. When a `/api/v1/conversations` (+ messages)
 * endpoint lands, swap the mock store for SDK calls; these types describe the
 * intended shape.
 *
 * The thread identity block at the bottom ({@link ThreadKey},
 * {@link threadKeyOf}, {@link threadKeyOfEnvelope}) is phase6-contract.md
 * §3.2.2: the ONE key the messages store, the outbox scope and the realtime
 * reducer agree on for a conversation.
 */
import type { RealtimeEnvelope } from '@/contracts/realtime-envelope-v1';

/** Direct (1:1) chat vs. a group chat. */
export type ConversationKind = 'direct' | 'group';

/** Presence of a person — drives the avatar status dot + header subtitle. */
export type PresenceStatus = 'online' | 'away' | 'offline';

/** Delivery state of one of *my* outgoing messages — drives the check marks. */
export type MessageStatus = 'sent' | 'delivered' | 'read';

/** Avatar accent — soft tint + colored initials (direct) / icon tile (group). */
export type ConversationVariant = 'teal' | 'purple' | 'blue' | 'orange' | 'green';

/** Sentinel author id for the current (signed-in) user. */
export const ME = 'me' as const;

/** One file attached to a message (resolved to a servable URL server-side). */
export interface MessageAttachment {
  name: string;
  /** Absolute URL to view/download the file (signed S3 or origin-resolved). */
  url: string;
  /** Byte size when known (0 otherwise). */
  size: number;
  /** `image` renders an inline thumbnail; `file` renders a download chip. */
  kind: 'image' | 'file';
}

/** A single message inside a conversation thread. */
export interface ChatMessage {
  id: string;
  /** `ME` for the current user, otherwise the sender's participant id. */
  authorId: string;
  /** Display name of the sender (shown above incoming group bubbles). */
  authorName: string;
  /** Body copy. May contain `\n` line breaks (rendered with pre-wrap). */
  body: string;
  /** Pre-formatted clock label, e.g. "9:28 AM". */
  timeLabel: string;
  /** Sortable timestamp (ms). */
  sentAt: number;
  /** Files attached to the message (images + documents). Always set by the API mapper. */
  attachments?: MessageAttachment[];
  /** Delivery state — only meaningful for my outgoing messages. */
  status?: MessageStatus;
  /** Marks the first unread incoming message (renders the divider above it). */
  firstUnread?: boolean;
  /**
   * The sender has LEFT the company (server-decided: an inactive or deleted staff
   * account). {@link authorName} is already "Resigned Employee #<job id>"; render a
   * neutral placeholder avatar and never open their profile. Never set on my own
   * messages; absent means false (and on an older server).
   */
  authorSeparated?: boolean;
}

/**
 * A participant in a group chat — rendered in the member shelf at the top of the
 * Group conversation page and in the full Participants page.
 *
 * Today this roster is derived from the thread's message senders (the end-user
 * API exposes no group-members endpoint), so {@link role} / {@link isAdmin} are
 * unset and {@link lastActiveLabel} carries the only activity signal. Once the
 * backend grows `GET /me/message-groups/{id}/members`, the richer fields light
 * up automatically (see `mapGroupMembers`).
 */
export interface GroupMember {
  id: string;
  name: string;
  initials: string;
  /** Absolute avatar URL, or '' — render with the initials fallback. */
  image: string;
  variant: ConversationVariant;
  /** True when this participant is the signed-in user (renders a "You" tag). */
  isMe: boolean;
  /** Job title / role — only the real members endpoint provides it. */
  role?: string;
  /** Group admin flag — only the real members endpoint provides it. */
  isAdmin?: boolean;
  /** Relative label of this member's most recent message in the thread. */
  lastActiveLabel?: string;
  /** Sortable ms of that most recent message — drives "Recently active" sort. */
  lastActiveAt?: number;
  /**
   * This participant has left the company: {@link name} is "Resigned Employee
   * #<job id>", {@link image} is '', and their profile must not open.
   */
  separated?: boolean;
}

/** A conversation (thread or group chat) shown in the list and on its page. */
export interface Conversation {
  id: string;
  kind: ConversationKind;
  /**
   * Direct only — the OTHER person's user id (the list row's `counterpart.id`),
   * '' for groups. This is what the wire's `messages.thread` action carries as
   * `thread_id` for a direct thread, so it is the join between a realtime
   * envelope and this row ({@link buildThreadIndex}).
   */
  counterpartId: string;
  /** Display title — person name (direct) or group name (group). */
  name: string;
  /**
   * Direct only — the thread's subject/title, shown in grey beneath the
   * person's name in the conversation header. Omitted when absent or identical
   * to {@link name} (i.e. when `name` already fell back to the subject).
   */
  subject?: string;
  /** Pre-computed avatar initials. */
  initials: string;
  variant: ConversationVariant;
  /** Direct only — the other person's presence (omitted for groups). */
  presence?: PresenceStatus;
  /** Group only — member-count label, e.g. "12 members". */
  membersLabel?: string;
  /** Group only — numeric participant count (the source of `membersLabel`). */
  memberCount?: number;
  /**
   * Group only — participants for the member shelf / Participants page. Sourced
   * from the thread's message senders today (a PARTIAL roster: silent members
   * are absent), so it can be shorter than {@link memberCount}. Upgrades to the
   * full list once a members endpoint exists.
   */
  members?: GroupMember[];
  /** Preview of the most recent message, shown in the list row. */
  lastMessage: string;
  /** Group only — who sent the last message, e.g. "Mark" (prefixes preview). */
  lastSender?: string;
  /** Pre-formatted relative label, e.g. "9:30 AM", "Yesterday", "May 30". */
  timeLabel: string;
  /** Sortable timestamp (ms) of the last activity — drives list ordering. */
  updatedAt: number;
  /** Unread message count — drives the list badge. 0 = none. */
  unread: number;
  pinned: boolean;
  /** Date separator shown at the top of the thread, e.g. "Today". */
  threadDateLabel?: string;
  /** Full ordered message thread for the conversation page. */
  messages: ChatMessage[];
  /**
   * Direct only — the other person has LEFT the company (the list row's
   * `counterpart.separated`, or the thread's). {@link name} is already "Resigned
   * Employee #<job id>". Always set on direct rows by the mapper; absent on groups.
   */
  counterpartSeparated?: boolean;
  /**
   * Direct only — whether a reply can be sent. false when the other person has
   * left the company (the thread is read-only; the server refuses a send with 403
   * `recipient_unavailable`). Absent (groups, older servers) means true.
   */
  canReply?: boolean;
}

/**
 * Whether a DIRECT thread's other person has left the company. The loaded thread
 * wins over the cached list row (which may be stale); before the thread loads,
 * the list row is all there is. Groups never are.
 */
export function directCounterpartSeparated(
  thread: Pick<Conversation, 'kind' | 'counterpartSeparated'> | null | undefined,
  listRow: Pick<Conversation, 'kind' | 'counterpartSeparated'> | null | undefined,
): boolean {
  if (thread) return thread.kind === 'direct' && thread.counterpartSeparated === true;
  return !!listRow && listRow.kind === 'direct' && listRow.counterpartSeparated === true;
}

/**
 * Whether a DIRECT thread is read-only — its composer is replaced by a note.
 * True when the other person has left the company, when the server said
 * `can_reply: false`, or when a send was just refused (`refusedBySend`: the
 * outbox holds a `recipient_unavailable` bubble). Groups never are.
 */
export function directThreadReadOnly(
  thread: Pick<Conversation, 'kind' | 'counterpartSeparated' | 'canReply'> | null | undefined,
  listRow: Pick<Conversation, 'kind' | 'counterpartSeparated' | 'canReply'> | null | undefined,
  refusedBySend: boolean,
): boolean {
  const kind = thread?.kind ?? listRow?.kind;
  if (kind === 'group') return false;
  if (refusedBySend) return true;
  if (thread) return thread.canReply === false || thread.counterpartSeparated === true;
  return !!listRow && listRow.counterpartSeparated === true;
}

/** A person you can start a new conversation with (compose modal). */
export interface Contact {
  id: string;
  name: string;
  initials: string;
  /** Absolute avatar URL, or ''/undefined — render with the initials fallback. */
  image?: string;
  variant: ConversationVariant;
  /** Short role/department label, e.g. "Finance". */
  role?: string;
  presence: PresenceStatus;
  /**
   * When this person was last active, in epoch ms — the value `presence` is derived from.
   * Lets a surface show "Last seen 12m ago" instead of only a grey dot. Absent when they have
   * never signed in.
   */
  lastOnline?: number;
  /** Existing direct conversation id, when one already exists. */
  conversationId?: string;
}

/* ── Thread identity (phase6-contract.md §3.2.2) ─────────────────────────── */

/**
 * The unified thread identity: `direct:<REST root message id>` (the direct
 * list row's `thread_id`, which is also `Conversation.id`) or
 * `group:<group id>` (the bare id — WITHOUT the app's `g-` route prefix).
 *
 * It keys the store's per-thread state and the outbox scope, so it must never
 * be built from the wire's direct `thread_id` (that is the COUNTERPART user id)
 * or from a reply's `message_id` (never list-known).
 */
export type ThreadKey = `direct:${string}` | `group:${string}`;

/**
 * Counterpart user id → the direct thread keys that person appears in, built
 * from the conversation list ({@link buildThreadIndex}). A person normally has
 * ONE direct thread, but the REST list can carry several roots for the same
 * counterpart; an envelope resolves only when exactly one candidate exists.
 */
export type ThreadIndex = ReadonlyMap<string, readonly ThreadKey[]>;

const DECIMAL_ID_RE = /^[0-9]{1,20}$/;

/** Build a thread key from its kind and REST identity (root message id / group id). */
export function threadKeyOf(kind: ConversationKind, id: string): ThreadKey {
  const clean = id.trim();
  return kind === 'group' ? `group:${clean}` : `direct:${clean}`;
}

/** Split a thread key back into its kind and REST identity. */
export function parseThreadKey(key: ThreadKey): { kind: ConversationKind; id: string } {
  return key.startsWith('group:')
    ? { kind: 'group', id: key.slice('group:'.length) }
    : { kind: 'direct', id: key.slice('direct:'.length) };
}

/**
 * The app-side conversation id a thread key maps to (`Conversation.id`): the
 * root message id for a direct thread, `g-<id>` for a group — the value the
 * thread pages read from their route and the store's rows carry.
 */
export function conversationIdOf(key: ThreadKey): string {
  const { kind, id } = parseThreadKey(key);
  return kind === 'group' ? `g-${id}` : id;
}

/** The inverse of {@link conversationIdOf}: a row's `kind` + `id` → its thread key. */
export function threadKeyOfConversation(conversation: Pick<Conversation, 'id' | 'kind'>): ThreadKey {
  return conversation.kind === 'group'
    ? threadKeyOf('group', conversation.id.startsWith('g-') ? conversation.id.slice(2) : conversation.id)
    : threadKeyOf('direct', conversation.id);
}

/** Index the direct conversations of a list by their counterpart user id. */
export function buildThreadIndex(conversations: readonly Pick<Conversation, 'id' | 'kind' | 'counterpartId'>[]): ThreadIndex {
  const index = new Map<string, ThreadKey[]>();
  for (const conversation of conversations) {
    if (conversation.kind !== 'direct') continue;
    const counterpart = conversation.counterpartId.trim();
    if (!counterpart) continue;
    const key = threadKeyOfConversation(conversation);
    const held = index.get(counterpart);
    if (held) {
      if (!held.includes(key)) held.push(key);
    } else {
      index.set(counterpart, [key]);
    }
  }
  return index;
}

/**
 * Resolve the thread an envelope's `messages.thread` action addresses.
 *
 * A group resolves directly (`group:<thread_id>`). A direct thread is addressed
 * on the wire by the COUNTERPART user id, so it resolves only through the
 * index built from the conversation list, and only when that person has
 * exactly one direct thread there — zero (a brand-new conversation) or several
 * candidates are `'unresolved'`, which the caller answers with ONE list refresh
 * per burst. An envelope without a `messages.thread` action (a
 * `notifications.view` read of a message notification, for instance) carries
 * no thread identity at all and is `'unresolved'` here.
 */
export function threadKeyOfEnvelope(envelope: RealtimeEnvelope, index: ThreadIndex): ThreadKey | 'unresolved' {
  const action = envelope.action;
  if (!action || action.route !== 'messages.thread') return 'unresolved';
  const threadId = (action.params.thread_id ?? '').trim();
  if (!DECIMAL_ID_RE.test(threadId)) return 'unresolved';
  const type = action.params.thread_type;
  if (type === 'group') return threadKeyOf('group', threadId);
  if (type !== 'direct') return 'unresolved';
  const candidates = index.get(threadId);
  return candidates && candidates.length === 1 && candidates[0] ? candidates[0] : 'unresolved';
}

/**
 * The thread page currently on screen, as that page itself names it.
 *
 * Registered by the page instead of being derived from the conversation list,
 * because the list is precisely what may not know the thread yet. A DIRECT
 * thread is named on the wire by the COUNTERPART's user id, and
 * {@link threadKeyOfEnvelope} can only translate that through the index built
 * from the fetched list: a brand-new conversation, a cold deep link, or a list
 * fetch still in flight all read `'unresolved'`, and so did a counterpart
 * holding two direct threads. The open page would then be toasted over while it
 * was already appending the very message the toast announced. The page knows
 * its counterpart directly, so it says so.
 *
 * `routeName` / `routeId` are what the registration is CHECKED against, so one
 * left behind by a page Ionic keeps alive is inert rather than wrong.
 */
export interface OpenThreadRef {
  /** Route name this registration belongs to (`conversation` / `group-conversation`). */
  routeName: string;
  /** That route's `id` param — the app-side conversation id. */
  routeId: string;
  kind: 'direct' | 'group';
  /**
   * How the WIRE names this thread: a group's own id, or a direct thread's
   * COUNTERPART user id. Empty while the page cannot resolve it yet (a
   * send-only thread before its row loads); an empty id never matches, so the
   * index/route fallback still decides.
   */
  wireThreadId: string;
}

/**
 * Does this envelope address `open`'s thread? Compares on the identity the WIRE
 * uses — a group's own id, a direct thread's counterpart user id — so unlike
 * {@link threadKeyOfEnvelope} it needs no conversation list, which is the whole
 * point of the registration.
 */
export function envelopeAddressesOpenThread(envelope: RealtimeEnvelope, open: OpenThreadRef): boolean {
  const action = envelope.action;
  if (!action || action.route !== 'messages.thread') return false;
  if (!open.wireThreadId || action.params.thread_type !== open.kind) return false;
  const threadId = (action.params.thread_id ?? '').trim();
  return DECIMAL_ID_RE.test(threadId) && threadId === open.wireThreadId;
}
